import { useUnit } from "@/components/unit-provider";
import { Droplets, Wind, Cloud, Thermometer, Eye, Snowflake, CloudRain } from "lucide-react";
import { useState } from "react";

interface DailyForecastProps {
  daily: {
    dt: number;
    temp: { min: number; max: number; day: number; night: number; eve: number; morn: number };
    feels_like?: { day: number; night: number; eve: number; morn: number };
    weather: { icon: string; description: string }[];
    pop: number;
    humidity?: number;
    wind_speed?: number;
    wind_gust?: number;
    uvi?: number;
    sunrise?: number;
    sunset?: number;
    dew_point?: number;
    clouds?: number;
    rain?: number;
    snow?: number;
    summary?: string;
    pressure?: number;
  }[];
}

export function DailyForecast({ daily }: DailyForecastProps) {
  const { display } = useUnit();
  const days = daily.slice(0, 7);
  const [expanded, setExpanded] = useState<number | null>(null);

  const allMin = Math.min(...days.map((d) => d.temp.min));
  const allMax = Math.max(...days.map((d) => d.temp.max));
  const range = allMax - allMin || 1;

  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold">7-Day Outlook</h3>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Click to expand</span>
      </div>

      <div className="divide-y divide-border/40">
        {days.map((d, i) => {
          const date = new Date(d.dt * 1000);
          const dayName = i === 0 ? "Today" : date.toLocaleDateString([], { weekday: "short" });
          const dateStr = date.toLocaleDateString([], { month: "short", day: "numeric" });
          const icon = d.weather?.[0]?.icon || "01d";
          const desc = d.weather?.[0]?.description || "";
          const isExpanded = expanded === i;
          const leftPct = ((d.temp.min - allMin) / range) * 100;
          const widthPct = ((d.temp.max - d.temp.min) / range) * 100;

          return (
            <div key={d.dt}>
              <button
                onClick={() => setExpanded(isExpanded ? null : i)}
                className="w-full grid grid-cols-[3.5rem_2.5rem_1fr_auto_4rem] items-center gap-3 px-4 py-2.5 hover:bg-muted/40 transition-colors text-left"
              >
                <div>
                  <p className={`text-sm font-medium ${i === 0 ? "text-primary" : ""}`}>{dayName}</p>
                  <p className="text-[10px] text-muted-foreground">{dateStr}</p>
                </div>

                <img src={`/api/icon/${icon}.png`} alt={desc} className="h-9 w-9" title={desc} />

                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-xs text-muted-foreground w-14 text-right">{display(d.temp.min)}</span>
                  <div className="relative flex-1 h-1.5 rounded-full bg-muted">
                    <div
                      className="absolute h-full rounded-full bg-gradient-to-r from-blue-400 via-primary to-amber-400"
                      style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 5)}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs font-semibold w-14">{display(d.temp.max)}</span>
                </div>

                <div className="flex items-center gap-1 w-12 justify-end">
                  {d.pop > 0.05 ? (
                    <>
                      <Droplets className="h-3 w-3 text-blue-400" />
                      <span className="text-[11px] text-blue-400 font-medium font-mono">{Math.round(d.pop * 100)}%</span>
                    </>
                  ) : (
                    <span className="text-[11px] text-muted-foreground/30">—</span>
                  )}
                </div>

                <div className="text-right">
                  <span className="text-[10px] text-muted-foreground capitalize line-clamp-1">{desc}</span>
                </div>
              </button>

              {isExpanded && (
                <div className="bg-muted/20 border-t border-border/30 px-4 py-3">
                  {d.summary && (
                    <p className="text-xs text-foreground/70 mb-3 italic">{d.summary}</p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <Detail label="Morning" value={display(d.temp.morn)} icon={Thermometer} />
                    <Detail label="Day" value={display(d.temp.day)} icon={Thermometer} />
                    <Detail label="Evening" value={display(d.temp.eve)} icon={Thermometer} />
                    <Detail label="Night" value={display(d.temp.night)} icon={Thermometer} />
                    {d.feels_like?.day && <Detail label="Feels Like" value={display(d.feels_like.day)} />}
                    {d.humidity !== undefined && <Detail label="Humidity" value={`${d.humidity}%`} icon={Droplets} />}
                    {d.wind_speed !== undefined && <Detail label="Wind" value={`${d.wind_speed.toFixed(1)} m/s`} icon={Wind} />}
                    {d.wind_gust !== undefined && <Detail label="Gust" value={`${d.wind_gust.toFixed(1)} m/s`} icon={Wind} />}
                    {d.uvi !== undefined && <Detail label="UV Index" value={d.uvi.toFixed(1)} />}
                    {d.dew_point !== undefined && <Detail label="Dew Point" value={display(d.dew_point)} />}
                    {d.clouds !== undefined && <Detail label="Clouds" value={`${d.clouds}%`} icon={Cloud} />}
                    {d.pressure !== undefined && <Detail label="Pressure" value={`${d.pressure} hPa`} />}
                    {d.rain !== undefined && <Detail label="Rain" value={`${d.rain.toFixed(1)} mm`} icon={CloudRain} />}
                    {d.snow !== undefined && d.snow > 0 && <Detail label="Snow" value={`${d.snow.toFixed(1)} mm`} icon={Snowflake} />}
                    {d.sunrise && d.sunset && (
                      <Detail
                        label="Day length"
                        value={`${Math.floor((d.sunset - d.sunrise) / 3600)}h ${Math.floor(((d.sunset - d.sunrise) % 3600) / 60)}m`}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Detail({ label, value, icon: Icon }: { label: string; value: string; icon?: typeof Droplets }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-mono text-sm font-semibold mt-0.5">{value}</p>
    </div>
  );
}
